import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useZeitwahl } from '../provider';
import {
  addDays,
  addMonths,
  daysInMonth,
  firstDayOfWeek,
  monthGrid,
  monthOf,
  parseISODate,
  toISODate,
  weekdayLabels,
  weekdayOf,
  type ISODate,
  type ISOMonth,
} from '../time/calendar';
import { formatDayLong, formatMonthTitle } from '../time/format';
import { Chevron } from './notice';

export interface MonthCalendarProps {
  month: ISOMonth;
  onMonthChange: (month: ISOMonth) => void;
  selected: ISODate | null;
  onSelect: (date: ISODate) => void;
  /** Days that have at least one slot; every other day renders disabled. */
  availableDates: ReadonlySet<ISODate>;
  loading?: boolean;
  /** Navigation bounds (inclusive). */
  minMonth?: ISOMonth;
  maxMonth?: ISOMonth;
  /** Today in the visitor's zone, marked with `aria-current="date"`. */
  today?: ISODate;
  locale?: string;
}

/** Keeps the day-of-month when paging, clamped to the target month's length. */
function sameDayInMonth(date: ISODate, months: number): ISODate {
  const target = addMonths(monthOf(date), months);
  const [y, m] = parseISODate(`${target}-01`);
  const day = Math.min(parseISODate(date)[2], daysInMonth(target));
  return toISODate(y, m, day);
}

/**
 * `role="grid"` month view with a roving tabindex: arrows move by day/week, Home/End jump within
 * the week, PageUp/PageDown change the month, Enter/Space select (native button activation).
 */
export function MonthCalendar(props: MonthCalendarProps): ReactNode {
  const {
    month,
    onMonthChange,
    selected,
    onSelect,
    availableDates,
    loading,
    minMonth,
    maxMonth,
    today,
  } = props;
  const { t, locale: contextLocale } = useZeitwahl();
  const locale = props.locale ?? contextLocale;
  const firstDay = useMemo(() => firstDayOfWeek(locale), [locale]);
  const grid = useMemo(() => monthGrid(month, firstDay), [month, firstDay]);
  const headers = useMemo(() => weekdayLabels(locale, firstDay), [locale, firstDay]);
  const headingId = useId();
  const gridRef = useRef<HTMLTableElement>(null);
  const pendingFocus = useRef<ISODate | null>(null);
  // `null` means "derive": the selected day, else today, else the first available day of the month.
  const [focusDate, setFocusDate] = useState<ISODate | null>(null);

  const inMonth = (d: ISODate | null | undefined): d is ISODate => !!d && monthOf(d) === month;
  const firstAvailable = useMemo(
    () => [...availableDates].filter((d) => monthOf(d) === month).sort()[0],
    [availableDates, month]
  );
  const activeDate: ISODate = inMonth(focusDate)
    ? focusDate
    : inMonth(selected)
      ? selected
      : inMonth(today)
        ? today
        : (firstAvailable ?? `${month}-01`);

  useEffect(() => {
    const target = pendingFocus.current;
    if (!target) return;
    pendingFocus.current = null;
    gridRef.current?.querySelector<HTMLElement>(`[data-date="${target}"]`)?.focus();
  });

  const canPrev = !minMonth || addMonths(month, -1) >= minMonth;
  const canNext = !maxMonth || addMonths(month, 1) <= maxMonth;

  const navigate = (delta: number) => {
    setFocusDate(null);
    onMonthChange(addMonths(month, delta));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    const offset = (weekdayOf(activeDate) - firstDay + 7) % 7;
    let target: ISODate;
    switch (event.key) {
      case 'ArrowRight':
        target = addDays(activeDate, 1);
        break;
      case 'ArrowLeft':
        target = addDays(activeDate, -1);
        break;
      case 'ArrowDown':
        target = addDays(activeDate, 7);
        break;
      case 'ArrowUp':
        target = addDays(activeDate, -7);
        break;
      case 'Home':
        target = addDays(activeDate, -offset);
        break;
      case 'End':
        target = addDays(activeDate, 6 - offset);
        break;
      case 'PageUp':
        target = sameDayInMonth(activeDate, -1);
        break;
      case 'PageDown':
        target = sameDayInMonth(activeDate, 1);
        break;
      default:
        return;
    }
    event.preventDefault();
    const targetMonth = monthOf(target);
    if (targetMonth !== month) {
      if ((minMonth && targetMonth < minMonth) || (maxMonth && targetMonth > maxMonth)) return;
      onMonthChange(targetMonth);
    }
    setFocusDate(target);
    pendingFocus.current = target;
  };

  return (
    <div className="zw-cal">
      <div className="zw-cal-nav">
        <button
          type="button"
          className="zw-cal-navbtn"
          aria-label={t('calendarPrevMonth')}
          disabled={!canPrev}
          onClick={() => navigate(-1)}
        >
          <Chevron direction="left" />
        </button>
        <h3 className="zw-cal-title" id={headingId} aria-live="polite">
          {formatMonthTitle(month, locale)}
        </h3>
        <button
          type="button"
          className="zw-cal-navbtn"
          aria-label={t('calendarNextMonth')}
          disabled={!canNext}
          onClick={() => navigate(1)}
        >
          <Chevron direction="right" />
        </button>
      </div>
      <table
        role="grid"
        className="zw-cal-grid"
        aria-labelledby={headingId}
        aria-busy={loading || undefined}
        ref={gridRef}
        onKeyDown={onKeyDown}
      >
        <thead>
          <tr role="row">
            {headers.map((h) => (
              <th
                key={h.long}
                role="columnheader"
                scope="col"
                className="zw-cal-weekday"
                aria-label={h.long}
              >
                <span aria-hidden="true">{h.short}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.map((row, r) => (
            <tr key={r} role="row">
              {row.map((cell) => {
                if (!cell.inMonth) {
                  return (
                    <td
                      key={cell.date}
                      role="gridcell"
                      className="zw-cal-cell zw-cal-cell--empty"
                    />
                  );
                }
                const available = availableDates.has(cell.date);
                const isSelected = selected === cell.date;
                const isToday = today === cell.date;
                const label =
                  formatDayLong(cell.date, locale) +
                  (isToday ? `, ${t('calendarToday')}` : '') +
                  (available ? '' : `, ${t('calendarDayUnavailable')}`);
                return (
                  <td
                    key={cell.date}
                    role="gridcell"
                    className="zw-cal-cell"
                    aria-selected={isSelected}
                  >
                    <button
                      type="button"
                      className={`zw-cal-day${isSelected ? ' zw-cal-day--selected' : ''}${
                        available ? ' zw-cal-day--available' : ''
                      }${isToday ? ' zw-cal-day--today' : ''}`}
                      data-date={cell.date}
                      tabIndex={cell.date === activeDate ? 0 : -1}
                      aria-disabled={available ? undefined : true}
                      aria-current={isToday ? 'date' : undefined}
                      aria-label={label}
                      onClick={() => {
                        setFocusDate(cell.date);
                        if (available) onSelect(cell.date);
                      }}
                    >
                      {parseISODate(cell.date)[2]}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && !firstAvailable && (
        <p className="zw-cal-empty zw-muted">{t('calendarNoDates')}</p>
      )}
    </div>
  );
}
